import type { QueryClient } from '@tanstack/vue-query'

import type {
  IChannel,
  IChannelSection,
  IChannelsWithSectionsResponse,
  MoveChannelSectionMutationInput,
} from '../types'
import type { channelsQueryKeys as queryKeys } from './query-keys.utils'

export const restoreChannelSectionsListQueryData = (
  queryClient: QueryClient,
  listKey: ReturnType<typeof queryKeys.list>,
  previousList?: IChannelsWithSectionsResponse
) => {
  if (previousList) {
    queryClient.setQueryData(listKey, previousList)
    return
  }

  queryClient.removeQueries({
    queryKey: listKey,
    exact: true,
  })
}

export const appendSectionToListQueryData = (
  data: IChannelsWithSectionsResponse,
  section: IChannelSection
): IChannelsWithSectionsResponse => {
  return {
    ...data,
    sections: [...data.sections, section],
  }
}

export const updateSectionInListQueryData = (
  data: IChannelsWithSectionsResponse,
  sectionId: string,
  updates: Partial<Pick<IChannelSection, 'name' | 'icon'>>
): IChannelsWithSectionsResponse => {
  return {
    ...data,
    sections: data.sections.map(section => {
      if (section._id !== sectionId) {
        return section
      }

      return {
        ...section,
        ...updates,
      }
    }),
  }
}

export const removeSectionFromListQueryData = (
  data: IChannelsWithSectionsResponse,
  sectionId: string
): IChannelsWithSectionsResponse => {
  return {
    ...data,
    channels: data.channels.map(channel => {
      if (channel.sectionId !== sectionId) {
        return channel
      }

      return {
        ...channel,
        sectionId: null,
        orderInSection: null,
      }
    }),
    sections: data.sections.filter(section => section._id !== sectionId),
  }
}

export const updateChannelInSectionsResponse = (
  data: IChannelsWithSectionsResponse,
  channelId: string,
  updates: Partial<IChannel>
): IChannelsWithSectionsResponse => {
  return {
    ...data,
    channels: data.channels.map(channel => {
      if (channel._id !== channelId) {
        return channel
      }

      return {
        ...channel,
        ...updates,
      }
    }),
  }
}

export const applyOptimisticSectionPlacement = (
  data: IChannelsWithSectionsResponse,
  placement: MoveChannelSectionMutationInput
): IChannelsWithSectionsResponse => {
  if (!placement.targetOrderedIds.includes(placement.sectionId)) {
    return data
  }

  const isRootTarget = placement.targetParentSectionId === null
  const targetOrderMap = new Map(placement.targetOrderedIds.map((id, index) => [id, index + 1]))

  return {
    ...data,
    sections: data.sections.map(section => {
      const currentSectionId = section._id || ''
      const isMovedSection = currentSectionId === placement.sectionId
      const targetOrder = targetOrderMap.get(currentSectionId)
      const parentSectionId = isMovedSection ? placement.targetParentSectionId : (section.parentSectionId ?? null)

      if (!targetOrder || parentSectionId !== placement.targetParentSectionId) {
        return isMovedSection
          ? {
              ...section,
              parentSectionId: placement.targetParentSectionId,
            }
          : section
      }

      return {
        ...section,
        parentSectionId,
        sequence: isRootTarget ? targetOrder : null,
        orderInSection: isRootTarget ? null : targetOrder,
      }
    }),
    channels: data.channels.map(channel => {
      const channelId = channel._id || ''
      const targetOrder = targetOrderMap.get(channelId)
      const channelParentSectionId = channel.sectionId ?? null

      if (!targetOrder || channelParentSectionId !== placement.targetParentSectionId) {
        return channel
      }

      return {
        ...channel,
        sequence: isRootTarget ? targetOrder : null,
        orderInSection: isRootTarget ? null : targetOrder,
      }
    }),
  }
}
